/**
 * Team messaging — internal staff chat with DMs, groups, mentions, and
 * presence. All endpoints sit under `/api/team/`.
 */

import { request, buildQuery, API_BASE_URL } from './client'
import { useAuthStore } from '@/store/auth'

// ── Types ──────────────────────────────────────────────────────────

export interface TeamChannel {
  id: string
  kind: 'dm' | 'group'
  name: string | null
  description: string | null
  members: string[]
  created_by: string
  created_at: string
  updated_at: string
  last_message_at?: string | null
  unread_count?: number
}

export interface TeamMessage {
  id: string
  channel_id: string
  sender_id: string
  sender_email: string
  sender_name: string | null
  body: string
  mentions: EntityMention[]
  edited_at: string | null
  created_at: string
}

export interface EntityMention {
  kind: string
  id: string
  display: string
}

export interface StaffMember {
  id: string
  email: string
  display_name: string | null
  roles: string[]
  online: boolean
}

export interface EntityResult {
  kind: string
  id: string
  display: string
  subtitle?: string
}

export type TeamWsEvent =
  | { type: 'Message'; message: TeamMessage }
  | { type: 'Typing'; channel_id: string; user_id: string; is_typing: boolean }
  | { type: 'PresenceChange'; user_id: string; online: boolean }
  | { type: 'ChannelCreated'; channel: TeamChannel }
  | { type: 'ReadReceipt'; channel_id: string; user_id: string; last_read_at: string }

// ── WebSocket URL ──────────────────────────────────────────────────

export function teamWsUrl(): string {
  const token = useAuthStore.getState().token
  const base = API_BASE_URL.replace(/^http/, 'ws')
  const qs = new URLSearchParams()
  if (token) qs.set('token', token)
  return `${base}/api/team/ws${qs.toString() ? `?${qs.toString()}` : ''}`
}

// ── REST helpers ───────────────────────────────────────────────────

export async function listChannels(): Promise<{ channels: TeamChannel[] }> {
  return request<{ channels: TeamChannel[] }>('/api/team/channels')
}

export async function createChannel(body: {
  kind: 'dm' | 'group'
  name?: string
  members: string[]
}): Promise<TeamChannel> {
  return request<TeamChannel>('/api/team/channels', {
    method: 'POST',
    body,
  })
}

export async function getChannel(id: string): Promise<TeamChannel> {
  return request<TeamChannel>(`/api/team/channels/${encodeURIComponent(id)}`)
}

export async function listMessages(
  channelId: string,
  params?: { before?: string; limit?: number },
): Promise<{ messages: TeamMessage[] }> {
  return request<{ messages: TeamMessage[] }>(
    `/api/team/channels/${encodeURIComponent(channelId)}/messages${buildQuery({
      before: params?.before,
      limit: params?.limit ?? 50,
    })}`,
  )
}

export async function sendMessage(
  channelId: string,
  body: string,
  mentions?: EntityMention[],
): Promise<TeamMessage> {
  return request<TeamMessage>(
    `/api/team/channels/${encodeURIComponent(channelId)}/messages`,
    { method: 'POST', body: { body, mentions } },
  )
}

export async function markChannelRead(channelId: string): Promise<void> {
  await request(`/api/team/channels/${encodeURIComponent(channelId)}/read`, {
    method: 'POST',
  })
}

export async function listOnlineUsers(): Promise<{ online: string[] }> {
  return request<{ online: string[] }>('/api/team/online')
}

export async function listStaff(): Promise<{ staff: StaffMember[] }> {
  return request<{ staff: StaffMember[] }>('/api/team/staff')
}

export async function searchEntities(
  q: string,
  kind?: string,
): Promise<{ results: EntityResult[] }> {
  return request<{ results: EntityResult[] }>(
    `/api/team/search${buildQuery({ q, kind })}`,
  )
}
